import { Injectable } from '@angular/core';
import type { FeatureTypes, ComplexFeatureFlagValue } from './feature-flag-types';
import { UADetect as UADetectClass } from 'go-modules/detect/ua-detect.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

// IMPORTANT: if adding a new feature test that relies on a client settings property
// that is set on the frontend like orgList or currentFolder
// be sure to preserve that property in watchForSettingChanges as that resets clientSettings every 15 minutes

@Injectable({
	providedIn: 'root'
})
export class FeatureTestsService {

	constructor (private UADetect: UADetectClass) {}

	/**
	 * How to use userSegment flag
	 * add a `userSegment` section to feature flags
	 * The object can look as follows:
	 * {
	 *   <num-from-1-100>: <true/false/other-flag-config>,
	 *   DEFAULT: {} <other-flag-config>
	 * }
	 * You must always include a DEFAULT, which is the equivalent to "100"
	 * To target a percentage of our users, use a num from 1-100: true
	 * You can simulate A/B testing with two feature flags, one set to 50: true, DEFAULT: false
	 * and the other set to 50: false, DEFAULT: true
	 */
	public userSegment (userSegments: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		type NumericKeyEntry<T> = [number, T];

		// Convert the keys from strings to ints, and replace default with 100
		const numericSegments = Object.entries(userSegments)
			.map(<T>([key, value]): NumericKeyEntry<T> => [
				key === 'DEFAULT' ? 100 : parseInt(key, 10),
				value
			])
			.sort(([a], [b]) => a - b);

		const gcd = numericSegments
			.map(([percentage]) => percentage)
			.reduce(this.findGCD);

		// Determine which segment the user falls into based on their ID
		const modDivisor = 100 / gcd;
		const userIdMod = clientSettings.userId % modDivisor;

		// Find the first segment whose normalized percentage is greater than the user's bucket
		const [, segmentValue] = numericSegments
			.find(([percentage]) => userIdMod < percentage / gcd);

		return next(segmentValue);
	}

	public browsers (browsers: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		const browserName = this.UADetect.browserDetector.browser.uniqueName.toUpperCase();
		const hasBrowserTest = browserName in browsers;
		if (!hasBrowserTest) {
			return next(browsers.DEFAULT);
		}
		return next(browsers[browserName]);
	}

	// see possible values https://docs.uaparser.dev/info/os/name.html
	// when putting in a flag should be all uppercase and replace spaces with underscores
	public operatingSystems (operatingSystems: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		const osName = this.UADetect.browserDetector.result.os.name.toUpperCase().replaceAll(' ', '_');
		const hasOSTest = osName in operatingSystems;
		if (!hasOSTest) {
			return next(operatingSystems.DEFAULT);
		}
		return next(operatingSystems[osName]);
	}

	public partners (partners: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		const activePartners: string[] = (clientSettings.partners || []).map((value) => value.guid);
		const hasPartnerTest = activePartners.some((partner) => partner in partners);
		if (!hasPartnerTest) {
			return next(partners.DEFAULT);
		}
		return activePartners.some((partner) => next(partners[partner]));
	}

	public orgs (orgs: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		const activeOrgs: number[] = clientSettings.orgList || [];
		const hasOrgTest = activeOrgs.some((org) => org in orgs);
		if (!hasOrgTest) {
			return next(orgs.DEFAULT);
		}
		return activeOrgs.some((org) => next(orgs[org]));
	}

	public folders (folders: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {
		const selectedFolder = clientSettings.currentFolder ?? undefined;

		if (selectedFolder === undefined || folders[selectedFolder.group_id] === undefined) {
			return next(folders.DEFAULT);
		}

		return next(folders[selectedFolder.group_id]);
	}

	public orgTypes (orgsTypes: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {
		const orgSettings = clientSettings.orgSettings;

		if (!orgSettings) {
			return false;
		}

		const foundKey = Object.keys(orgsTypes).find((item) => orgSettings.org_type === item.toLowerCase());

		if (!foundKey) {
			return next(orgsTypes.DEFAULT);
		}

		return next(orgsTypes[foundKey]);
	}

	/**
	 * Originally you'd just do "releaseAt": 'some date', but that didn't work with the typings,
	 * nor allow you to specify multiple levels (i.e. releasing browsers at different times).
	 * The latest time that has past will be used.
	 *
	 * Now like everything else, releaseAt uses keys with a DEFAULT, where DEFAULT is the begining of time (1970)
	 */
	public releaseAt (releaseAt: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		return next(this.latestDateSegment(releaseAt));
	}

	/**
	 * Same as releaseAt.
	 */
	public userCreatedAfter (createdAt: ComplexFeatureFlagValue<FeatureTestsService>,
		next: (feature: FeatureTypes<FeatureTestsService>) => boolean) {

		return next(this.latestDateSegment(createdAt));
	}

	public latestDateSegment (segments: ComplexFeatureFlagValue<FeatureTestsService>) {
		type DateKeyEntry<T> = [dayjs.Dayjs, T];

		// Convert the keys from strings to dates, and replace default epoch
		const dateSegments = Object.entries(segments)
			.map(<T>([key, value]): DateKeyEntry<T> => [
				key === 'DEFAULT' ? dayjs(0) : dayjs.utc(key),
				value
			])
			.sort(([a], [b]) => b.diff(a));


		// Find the last segment that past the date
		const [, segmentValue] = dateSegments.find(([date]) => date.isBefore(dayjs()));

		return segmentValue;
	}

	// https://sourajeetm717.medium.com/euclids-gcd-method-iterative-and-recursive-6ee32935bcc8
	// https://en.wikipedia.org/wiki/Euclidean_algorithm#Implementations:~:text=%E2%88%921.-,Implementations,-%5Bedit%5D
	private findGCD (a: number, b: number): number {
		a = Math.abs(a);
		b = Math.abs(b);

		// Iterative Euclidean Algorithm (recursion begone!)
		while (b !== 0) {
			const temp = b;
			b = a % b;
			a = temp;
		}

		return a;
	}
}
